<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
            <!-- Header -->
            <div class="tw-relative tw-bg-gradient-to-br tw-from-primary-50 tw-to-primary-100/50 tw-px-6 tw-pt-8 tw-pb-6">
                <div class="tw-absolute tw-top-4 tw-right-4">
                    <button @click="onCancel" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                        <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                    </button>
                </div>

                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Company Logo / Icon -->
                    <div class="tw-p-2 tw-bg-white/80 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-overflow-hidden">
                        <img
                            v-if="companyLogo"
                            :src="companyLogo"
                            :alt="companyName"
                            class="tw-w-full tw-h-full tw-object-contain"
                        />
                        <icon v-else icon="solar:buildings-2-bold-duotone" class="tw-text-2xl tw-text-primary-500" />
                    </div>
                    <div>
                        <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">
                            Schedule a Meeting
                        </h2>
                        <p class="tw-text-sm tw-text-gray-600 tw-mt-1">
                            With {{ companyName || 'Company' }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Form Content -->
            <div class="tw-px-6 tw-py-6 tw-space-y-4">
                <!-- Title Field -->
                <FormField
                    v-model="formData.title"
                    type="text"
                    label="Meeting Title"
                    placeholder="e.g. Product demo, Partnership discussion..."
                    left-icon="solar:document-text-bold-duotone"
                    :required="true"
                    :error="errors.title"
                    help="Enter a descriptive title for your meeting"
                />

                <!-- Scheduled Date & Time -->
                <FormField
                    v-model="formData.scheduled_at"
                    type="datetime-local"
                    label="Scheduled Date & Time"
                    left-icon="solar:calendar-mark-bold-duotone"
                    :required="true"
                    :error="errors.scheduled_at"
                    help="Select when the meeting will take place"
                />

                <!-- Duration -->
                <FormField
                    v-model="formData.duration_minutes"
                    type="number"
                    label="Duration (minutes)"
                    placeholder="e.g., 60"
                    left-icon="solar:clock-circle-bold-duotone"
                    :error="errors.duration_minutes"
                    help="Meeting duration in minutes (15–480, optional)"
                />

                <!-- Location -->
                <FormField
                    v-model="formData.location"
                    type="text"
                    label="Location"
                    placeholder="e.g., Online, Office, Zoom link..."
                    left-icon="mingcute:location-fill"
                    :error="errors.location"
                    help="Where will the meeting take place? (optional)"
                />

                <!-- Description -->
                <FormField
                    v-model="formData.description"
                    type="textarea"
                    label="Description"
                    placeholder="Add any additional details about the meeting..."
                    left-icon="solar:notes-bold-duotone"
                    :error="errors.description"
                    rows="3"
                />
            </div>

            <!-- Footer -->
            <div class="tw-px-6 tw-pb-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button
                    intent="white"
                    :disabled="isLoading"
                    @click="onCancel"
                    class="tw-min-w-[100px]"
                >
                    Cancel
                </a-button>
                <a-button
                    intent="primary"
                    :disabled="isLoading || !isFormValid"
                    :loading="isLoading"
                    @click="handleSubmit"
                    left-icon="solar:calendar-bold-duotone"
                    class="tw-min-w-[140px]"
                >
                    Schedule Meeting
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import FormField from '@/components/form/FormField.vue';
import useAlert from '@/composables/useAlert';
import meetingsApi from '@/api/meetings';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    companyId: {
        type: [String, Number],
        required: true
    },
    companyName: {
        type: String,
        default: 'Company'
    },
    companyLogo: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['close', 'meeting-created']);

// Form state
const formData = ref({
    title: '',
    scheduled_at: '',
    duration_minutes: null,
    location: '',
    description: ''
});

const errors = ref({
    title: '',
    scheduled_at: '',
    duration_minutes: '',
    location: '',
    description: ''
});

const isLoading = ref(false);

// Computed
const isFormValid = computed(() => {
    return formData.value.title.trim().length > 0 &&
        formData.value.title.trim().length <= 255 &&
        formData.value.scheduled_at.length > 0;
});

// Reset when popup closes
watch(() => props.visible, (newVal) => {
    if (!newVal) resetForm();
});

// Methods
const resetForm = () => {
    formData.value = {
        title: '',
        scheduled_at: '',
        duration_minutes: null,
        location: '',
        description: ''
    };
    errors.value = {
        title: '',
        scheduled_at: '',
        duration_minutes: '',
        location: '',
        description: ''
    };
};

const validateForm = () => {
    let valid = true;
    errors.value = { title: '', scheduled_at: '', duration_minutes: '', location: '', description: '' };

    if (!formData.value.title.trim()) {
        errors.value.title = 'This field is required';
        valid = false;
    } else if (formData.value.title.trim().length > 255) {
        errors.value.title = 'Maximum length is 255 characters';
        valid = false;
    }

    if (!formData.value.scheduled_at) {
        errors.value.scheduled_at = 'This field is required';
        valid = false;
    } else {
        const scheduled = new Date(formData.value.scheduled_at);
        if (scheduled <= new Date()) {
            errors.value.scheduled_at = 'The meeting must be scheduled in the future';
            valid = false;
        }
    }

    if (formData.value.duration_minutes !== null && formData.value.duration_minutes !== '') {
        const dur = parseInt(formData.value.duration_minutes);
        if (isNaN(dur) || dur < 15 || dur > 480) {
            errors.value.duration_minutes = 'Duration must be between 15 and 480 minutes';
            valid = false;
        }
    }

    if (formData.value.location && formData.value.location.trim().length > 255) {
        errors.value.location = 'Maximum length is 255 characters';
        valid = false;
    }

    return valid;
};

const handleSubmit = async () => {
    if (!validateForm()) return;

    isLoading.value = true;
    try {
        const apiData = {
            company_id: props.companyId,
            title: formData.value.title.trim(),
            scheduled_at: new Date(formData.value.scheduled_at).toISOString()
        };

        if (formData.value.duration_minutes !== null && formData.value.duration_minutes !== '') {
            apiData.duration_minutes = parseInt(formData.value.duration_minutes);
        }
        if (formData.value.location && formData.value.location.trim()) {
            apiData.location = formData.value.location.trim();
        }
        if (formData.value.description && formData.value.description.trim()) {
            apiData.description = formData.value.description.trim();
        }

        const result = await meetingsApi.createMemberToCompany(apiData);
        useAlert('Meeting scheduled successfully!', 'success');
        emit('meeting-created', result.data);
        onCancel();
    } catch (err) {
        console.error('Error scheduling meeting:', err);
        const msg = err.response?.data?.message ||
            err.response?.data?.errors?.scheduled_at?.[0] ||
            err.message ||
            'Something went wrong. Please try again.';
        useAlert(msg, 'danger');
    } finally {
        isLoading.value = false;
    }
};

const onCancel = () => {
    resetForm();
    emit('close');
};
</script>
